import { rpcSlice } from '../../../store/api/rpcSlice';

// Types for artifact feedback
export type FeedbackType = 'positive' | 'negative';

export interface ArtifactFeedback {
  id: string;
  feedback_type: FeedbackType;
  feedback_text: string | null;
  created_at: string;
}

export interface LatestFeedbackResponse {
  feedback: ArtifactFeedback | null;
}

export interface CreateFeedbackRequest {
  feedback_type: FeedbackType;
  feedback_text?: string;
  context?: Record<string, unknown>;
}

export interface CreateFeedbackResponse {
  id: string;
  person_id: string;
  feedback_type: FeedbackType;
  feedback_text: string | null;
  context?: Record<string, unknown>;
  created_at: string;
}

export interface GetLatestFeedbackParams {
  artifact_id: string;
}

function contextArtifactId(context?: Record<string, unknown>): string | null {
  const artifactID = context?.artifact_id;
  if (typeof artifactID !== 'string') {
    return null;
  }
  const trimmed = artifactID.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export const artifactFeedbackApi = rpcSlice.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Get the latest feedback for the current user and artifact
     */
    getLatestArtifactFeedback: builder.query<LatestFeedbackResponse, GetLatestFeedbackParams>({
      query: ({ artifact_id }) => ({
        url: '/artifact/feedback.latest',
        method: 'GET',
        params: { artifact_id },
      }),
      providesTags: (_result, _err, arg) => [
        { type: 'ArtifactFeedback' as const, id: arg.artifact_id },
      ],
    }),

    /**
     * Create a new feedback record
     */
    createArtifactFeedback: builder.mutation<CreateFeedbackResponse, CreateFeedbackRequest>({
      query: (body) => ({
        url: '/artifact/feedback.create',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _err, arg) => {
        const artifactID = contextArtifactId(arg.context);
        return artifactID ? [{ type: 'ArtifactFeedback' as const, id: artifactID }] : [];
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetLatestArtifactFeedbackQuery,
  useLazyGetLatestArtifactFeedbackQuery,
  useCreateArtifactFeedbackMutation,
} = artifactFeedbackApi;
